<template>
  <div
    class="rw-autocomplete"
    role="combobox"
    aria-haspopup="listbox"
    aria-owns="rw-autocomplete-list"
    autocomplete="off"
    :aria-expanded="resultsDrawerShown"
    :class="wrapperClassObject"
  >
    <label
      id="rw-autocomplete-label"
      class="rwfc rwfc--select mb-0"
      for="rw-autocomplete-input"
      :class="{ 
        'rwfc--has-label': !hideLabel,
        'er': wrapperClassObject['er']
      }"
    >
      <span class="the-text" :class="{ 'sr-only': hideLabel }"
        >{{ labelText }}</span
      >
      <div class="rw-autocomplete__input-wrapper" :class="loading ? 'rw-autocomplete__input-wrapper--loading' : ''">
        <input
          autocapitalize="none"
          type="search"
          id="rw-autocomplete-input"
          class="rwfc__input"
          v-model="searchTerm"
          :placeholder="placeholderText"
          @focus="onFocus"
          @blur="onBlur"
          autocomplete="off"
          role="combobox"
          aria-owns="rw-autocomplete-list"
          aria-autocomplete="list"
          aria-controls="rw-autocomplete-results"
          @keydown.enter="onItemClick(0)"
          :aria-expanded="resultsDrawerShown"
          :aria-activedescendant="highlightedItem ? `rw-autocomplete-item-${highlightedItem.place_id}` : false "
        />
      </div>
    </label>

    <div
      id="rw-autocomplete-results-drawer"
      class="rw-autocomplete__results-drawer"
      :class="{ 'rw-autocomplete__results-drawer--shown': resultsDrawerShown }"
    >
      <ul
        id="rw-autocomplete-list"
        class="rw-autocomplete__list"
        role="listbox"
        aria-label="Results"
      >
        <template v-if="suggestions.length > 0">
          <li
            v-for="(suggestion, index) in suggestions"
            tabindex="-1"
            :id="`rw-autocomplete-item-${suggestion.place_id}`"
            :aria-selected="highlightedIndex == index ? 'true' : 'false'"
            :key="suggestion.place_id"
            role="option"
            class="rw-autocomplete__list__item d-flex text-nowrap"
            :class="{
              'rw-autocomplete__list__item--highlighted':
                highlightedIndex == index
            }"
            @click="onItemClick(index)"
            @keydown.enter="onItemClick(index)"
            :ref="`item-${index}`"
          >
            <!-- MUST BE WRAPPED IN AN ANCHOR TO BE SR-READABLE -->
            <span class="rw-autocomplete__list__item-inner">
              {{ suggestion.description }}
            </span>
          </li>
        </template>
        <template v-else>
          <li
            :id="'rw-autocomplete-item-0'"
            class="rw-autocomplete__list__item"
            :class="{
              'rw-autocomplete__list__item--highlighted': highlightedIndex === 0
            }"
            role="option"
            @click="onItemClick(suggestion.place_id)"
          >
            <template v-if="loading">
              Loading...
            </template>
            <template v-else>
              No results
            </template>
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>

<script>
import * as googlePlacesAPI from '../../../api/google-places';
export default {
  props: {
    hideLabel: {
      type: Boolean,
      default: false
    },
    addressOnly: {
      type: Boolean,
      default: false
    },
    labelText: {
      type: String,
      default: 'Enter your Address'
    },
    locationText: {
      type: String
    },
    labelText: {
      type: String,
      default: 'Enter Your Address'
    },
    placeholderText: {
      type: String,
      default: 'Enter Location',
    },
    wrapperClassObject: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },
  data() {
    return {
      loading: false,
      highlightedIndex: '', // needs to be an int so it can be incremented \ decreemnted
      search: '',
      lastSearch: '',
      searchMade: false,
      suggestions: [],
      resultsDrawerShown: false,

      keys: {
        right: 39,
        up: 38,
        left: 37,
        down: 40,
        tab: 9,
        enter: 13,
      }
      // pauseRequests: false,
    };
  },
  mounted() {
    const vm = this;

    /**
     * The autocomplete was initialized with a value pre-selected
     */
    if(this.locationText) {
      this.search = this.locationText;
    }

    document
      .querySelector('.rw-autocomplete')
      .addEventListener('click', function(e) {
        e.stopPropagation();
      });

    /**
     * Any time a click event bubbles up to the body level, call this event
     */
    document.querySelector('body').addEventListener('click', function() {
      vm.suggestions = [];
      vm.closeResults();
    });

    document.addEventListener('keyup', function(e) {
      // if the event did not come from these elements, ignore it
      if (
        !e.target.id == 'rw-autocomplete-input' &&
        !e.target.matches('.rw-autocomplete__list__link')
      ) {
        return false;
      }

      if (e.keyCode == vm.keys.down) {
        if (vm.highlightedIndex === '') {
          vm.highlightFirstItem();
        } else {
          vm.highlightNext();
        }
      }

      if (e.keyCode == vm.keys.up) {
        if (vm.highlightedIndex === '') {
          vm.highlightLastItem();
        } else {
          vm.highlightPrevious();
        }
      }

      if (e.keyCode == vm.keys.enter) {
        if (vm.highlightedIndex !== '') {
          vm.selectHighlightedItem();
        }
      }

      if (e.keyCode == vm.keys.tab) {
        if(e.target.id !== 'rw-autocomplete-input' && vm.resultsDrawerShown) {
          vm.closeResults();
        }
      }

    });
  },

  computed: {
    highlightedItem(){
      if(!this.suggestions.length) return false;
      return this.suggestions[this.highlightedIndex];
    },

    searchTerm: {
      get(){ return this.search; },
      set(value){
        this.searchMade = true;
        return this.search = value;
      }
    }
  },
  
  methods: {
    onUpdateSearchTerm(search) {
      if (!this.searchMade || search.length < 3) return false;
      this.loading = true;
      this.highlightedIndex = '';
      this.autocompleLoopup({ 
        search,
        addressOnly: this.addressOnly
      });
    },

    autocompleLoopup: _.debounce(function(params) {

      const vm = this;

      if (!params.search) {
        this.loading = false;
        return false;
      }



      googlePlacesAPI.autocomplete({ term: params.search, addressOnly: params.addressOnly })
        .then(res => {
          // enable the drawer
          this.loading = false;
          this.resultsDrawerShown = true;
          // if (res.data.suggestions && res.data.suggestions.length > 0) {
          //   vm.suggestions = res.data.suggestions;
          // }

          vm.suggestions = res.data.predictions;
        })
        .catch(err => {
          console.warn(err);
        });
      // return new Promise((resolve, reject) => {


      // });
      
    }, 200),

    onItemClick(id) {
      let selected = this.suggestions[id];

      // if this line is not here, enter key presses on other parts of the app were causing selections to be made in this component when no results were available
      if(!selected || !selected.description) {
        console.warn('invalid selection');
        return false;
      }
      this.$emit('selection', selected);
      // we are about to change the search term which will trigger a watcher that sends an api request. this flag will be returned to false when the watcher fires.
      this.pauseRequests = true;
      this.search = selected.description;
      this.resultsDrawerShown = false;
      // console.trace();
    },

    focusHighlightedItem() {
      let item = this.$refs[`item-${this.highlightedIndex}`][0];
      item.focus();
    },

    highlightFirstItem() {
      this.highlightedIndex = 0;
      // this.focusHighlightedItem();
    },

    highlightLastItem() {
      this.highlightedIndex = this.suggestions.length - 1;
      // this.focusHighlightedItem();
    },

    highlightNext(e) {
      if (this.highlightedIndex == this.suggestions.length - 1) {
        this.highlightedIndex = 0;
      } else {
        this.highlightedIndex++;
      }
      // this.focusHighlightedItem();
    },

    highlightPrevious(e) {
      if (this.highlightedIndex > 0) {
        this.highlightedIndex--;
        // this.focusHighlightedItem();
      } else {
        this.highlightLastItem(); // will also focus an anchor
      }
    },

    selectHighlightedItem() {
      this.onItemClick(this.highlightedIndex);
    },

    closeResults() {
      this.resultsDrawerShown = false;
      this.highlightedIndex = 0;
      this.$emit('results-drawr-closed');
    },

    onFocus(){
      // this.searchMade = false;
      this.lastSearch = this.search;
      // this.search = '';
      if(!this.search) return false;

      if(this.suggestions.length){
        this.resultsDrawerShown = true;
      } else {
        this.searchMade = true;
        this.onUpdateSearchTerm(this.search);
      }
    },

    onBlur(){
      this.searchMade = false;
      /**
       * Expectation: If User cleared the input by focusing it but did not enter anything, then blurred it. return to original search term
       */
      if(this.search == '' && this.lastSearch){
        this.search = this.lastSearch;
      }
    }

    
  },
  watch: {
    /** in case this was initialized before it had location text available to it */
    locationText(value) {
      // if(this.search) return false;
      this.search = value;
      // /**
      //  * A request is being sent to the API when the component initializes with a location already passed in. Need to prevent that. This will be set to false upon the next attempt to send a request to the API
      //  */
      // this.pauseRequests = true;
    },
    search(value) {
      if(!this.searchMade) return false;
      // /**
      //  * Requests were paused by a programmatic search term change. do not proceed. unpause requests so this can pass next time around
      //  */
      if (this.pauseRequests) {
        this.pauseRequests = false;
        return false;
      }
      this.suggestions = [];

      /**
       * User cleared their selection
       */
      if (value == '') {
        this.closeResults();
        return false;
      } else { 
        this.onUpdateSearchTerm(value);
      }
    }
  }
};
</script>